import { useTranslation } from 'react-i18next';
import { AlertCircle, Clock } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

type SubscriptionStatus = 'required' | 'requested';

interface SubscriptionNotificationProps {
  status?: SubscriptionStatus;
  fullWidth?: boolean;
  className?: string;
}

const SubscriptionNotification: React.FC<SubscriptionNotificationProps> = ({
  status = 'required',
  fullWidth = false,
  className = '',
}) => {
  const { t } = useTranslation();

  const config = {
    required: {
      icon: AlertCircle,
      title: t(
        'agent.page.no_subscription.title.translation',
        'Subscription Required'
      ),
      message: t(
        'agent.page.no_subscription.message.translation',
        'Your agent subscription is not active. You can edit settings, but you need an active subscription to take calls and manage appointments.'
      ),
      colors: {
        bg: 'bg-blue-50 dark:bg-blue-900/20',
        border: 'border-blue-200 dark:border-blue-800',
        icon: 'text-blue-600 dark:text-blue-400',
        title: 'text-blue-900 dark:text-blue-100',
        message: 'text-blue-800 dark:text-blue-200',
      },
    },
    requested: {
      icon: Clock,
      title: t(
        'subscription.requested.notification.title',
        'Subscription Pending'
      ),
      message: t(
        'subscription.requested.notification.message',
        'Your subscription request is being reviewed. You will be notified once it is approved.'
      ),
      colors: {
        bg: 'bg-amber-50 dark:bg-amber-900/20',
        border: 'border-amber-200 dark:border-amber-800',
        icon: 'text-amber-600 dark:text-amber-400',
        title: 'text-amber-900 dark:text-amber-100',
        message: 'text-amber-800 dark:text-amber-200',
      },
    },
  };

  const { icon: Icon, title, message, colors } = config[status];

  if (fullWidth) {
    return (
      <div
        className={`${colors.bg} border ${colors.border} rounded-lg p-3 w-full ${className}`}
      >
        <div className="flex items-start gap-3">
          <Icon className={`${colors.icon} flex-shrink-0 w-4 h-4 mt-0.5`} />
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold ${colors.title} text-sm mb-1`}>
              {title}
            </h3>
            <p className={`text-xs ${colors.message}`}>{message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`${colors.bg} border ${colors.border} rounded-lg p-2 px-3 flex items-center gap-2 w-fit cursor-pointer hover:opacity-80 transition-opacity ${className}`}
        >
          <Icon className={`${colors.icon} flex-shrink-0 w-4 h-4`} />
          <span className={`font-semibold ${colors.title} text-sm`}>
            {title}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        side="left"
        sideOffset={10}
        className="w-80"
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Icon className={`${colors.icon} flex-shrink-0 w-4 h-4`} />
            <h4 className={`font-semibold text-sm ${colors.title}`}>{title}</h4>
          </div>
          <p className={`text-sm ${colors.message}`}>{message}</p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SubscriptionNotification;
